import { baseApi } from "../../services/rtkApi/baseApi";
import type { PrintEdition, PrintFormat } from "./printProducts";

// AOTD days by number, print pre-orders and gathered trading cards, from n450s_backend
// (`/api/prints/*`). baseUrl already has "/api".

export interface AotdDay {
  number: number;
  aotdId: string;
  date: string;
  title: string;
  createdBy: string;
  /** The author's emoji, for "🧙‍♂️.n450s"; null when they have none. */
  authorEmoji: string | null;
  /** The author's quote, printed on every face; null (or missing, older backend) when there is none. */
  authorQuote?: string | null;
  /** null while that day's guessing game hides them. */
  emojis: string[] | null;
  filename: string;
  /** The engine version, "3.7" (shown as "aotd-3.7"); null on days from before versions. */
  engineVersion: string | null;
  /** The winning tap after unlock, in ms; null when no race was recorded. */
  authorOffsetMs: number | null;
  isAuthor: boolean;
  /** Who holds each original: free, taken by someone, or the signed-in author's own pre-order. */
  originals: Partial<Record<PrintFormat, "free" | "taken" | "mine">>;
  latestNumber: number;
}

/** A pre-order, or a gathered trading card (format "trading-card", quantity 1). */
export interface PrintPreorder {
  id: string;
  number: number;
  format: PrintFormat;
  edition: PrintEdition;
  quantity: number;
  createdAt: string;
  /** The set a gathered card was pre-ordered in; null while it is only gathered. */
  setId: string | null;
}

/** A print the member removed: they can never pre-order or gather it again. */
export interface PrintLock {
  number: number;
  format: PrintFormat;
  edition: PrintEdition;
}

export interface PreorderBody {
  number: number;
  format: PrintFormat;
  edition: PrintEdition;
  quantity: number;
  /** The member confirmed the promise. Needed for a new pre-order, not for a quantity change. */
  confirm?: boolean;
}

export interface GatheredSet {
  id: string;
  cardCount: number;
  createdAt: string;
  cards: PrintPreorder[];
}

export interface Gathered {
  /** Gathered and not in a set yet, newest day first. */
  cards: PrintPreorder[];
  sets: GatheredSet[];
  /** How many cards make a set (21). */
  needed: number;
}

const api = baseApi.enhanceEndpoints({ addTagTypes: ["PrintPreorders", "AotdDay", "Gathered"] });

export const printsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAotdByNumber: builder.query<AotdDay, number | "latest">({
      query: (n) => `/prints/day/${n}`,
      providesTags: (_r, _e, n) => [{ type: "AotdDay", id: String(n) }],
    }),
    /** Today's AOTD number once its art is finished, else null. */
    getTodayPrintNumber: builder.query<{ number: number | null }, void>({
      query: () => "/prints/today",
    }),
    /** Posters and wall cards (gathered cards are apart), and every print removed for good. */
    getMyPreorders: builder.query<{ preorders: PrintPreorder[]; locked: PrintLock[] }, number | void>({
      query: (n) => (n ? `/prints/preorders/mine?number=${n}` : "/prints/preorders/mine"),
      providesTags: ["PrintPreorders"],
    }),
    savePreorder: builder.mutation<{ preorder: PrintPreorder }, PreorderBody>({
      query: (body) => ({ url: "/prints/preorders", method: "POST", body }),
      invalidatesTags: ["PrintPreorders", "AotdDay"],
    }),
    /** Removes a pre-order or puts back a gathered card, for good. */
    cancelPreorder: builder.mutation<void, string>({
      query: (id) => ({ url: `/prints/preorders/${encodeURIComponent(id)}`, method: "DELETE" }),
      invalidatesTags: ["PrintPreorders", "AotdDay", "Gathered"],
    }),
    getMyGathered: builder.query<Gathered, void>({
      query: () => "/prints/gathered/mine",
      providesTags: ["Gathered"],
    }),
    gatherCard: builder.mutation<{ card: PrintPreorder }, { number: number; edition: PrintEdition; confirm: true }>({
      query: (body) => ({ url: "/prints/gathered", method: "POST", body }),
      invalidatesTags: ["Gathered", "AotdDay"],
    }),
    preorderSet: builder.mutation<{ set: GatheredSet }, void>({
      query: () => ({ url: "/prints/sets", method: "POST", body: { confirm: true } }),
      invalidatesTags: ["Gathered"],
    }),
    /** Removes a set, for good: every card in it. */
    cancelSet: builder.mutation<void, string>({
      query: (id) => ({ url: `/prints/sets/${encodeURIComponent(id)}`, method: "DELETE" }),
      invalidatesTags: ["Gathered", "PrintPreorders", "AotdDay"],
    }),
    recordScan: builder.mutation<void, { number: number; pst: string }>({
      query: (body) => ({ url: "/prints/scan", method: "POST", body }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAotdByNumberQuery,
  useGetTodayPrintNumberQuery,
  useGetMyPreordersQuery,
  useSavePreorderMutation,
  useCancelPreorderMutation,
  useGetMyGatheredQuery,
  useGatherCardMutation,
  usePreorderSetMutation,
  useCancelSetMutation,
  useRecordScanMutation,
} = printsApi;
